import type { ReactNode } from "react";
import { useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { Check, KeyRound, ShieldAlert, UserPlus, X } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { authService } from "@/services/auth.service";
import { platformControlService } from "@/services/platform-control.service";
import { useAuthStore } from "@/stores/authStore";
import toast from "react-hot-toast";

const tabs = [
  "Approvals",
  "Scoped access",
  "RBAC changes",
  "Invitations",
  "Sessions",
  "Admin MFA",
  "SSO",
] as const;

export default function PlatformControlPage() {
  const [tab, setTab] = useState<(typeof tabs)[number]>("Approvals");
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [approvals, scopes, rbac, invitations, sessions, sso] = useQueries({
    queries: [
      {
        queryKey: ["platform-approvals"],
        queryFn: () => platformControlService.approvals(),
      },
      {
        queryKey: ["platform-scopes"],
        queryFn: platformControlService.scopeGrants,
      },
      {
        queryKey: ["platform-rbac-changes"],
        queryFn: platformControlService.rbacChangeSets,
      },
      {
        queryKey: ["platform-invitations"],
        queryFn: platformControlService.invitations,
      },
      {
        queryKey: ["platform-sessions"],
        queryFn: platformControlService.sessions,
      },
      {
        queryKey: ["platform-sso"],
        queryFn: platformControlService.ssoProviders,
      },
    ],
  });
  const refresh = () =>
    queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0]).startsWith("platform-"),
    });
  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: "APPROVE" | "REJECT";
    }) =>
      platformControlService.decideApproval(
        id,
        decision,
        window.prompt("Decision note (minimum 8 characters)") || "",
      ),
    onSuccess: () => {
      void refresh();
      toast.success("Approval updated");
    },
    onError: () => toast.error("Approval decision failed"),
  });
  const revokeSession = useMutation({
    mutationFn: (id: string) =>
      platformControlService.revokeSession(
        id,
        "Administrator revoked this session",
      ),
    onSuccess: () => {
      void refresh();
      toast.success("Session revoked");
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Security & approvals"
        subtitle="Maker-checker decisions, scoped access, secure invitations, MFA and active sessions."
      />
      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2">
        {tabs.map((item) => (
          <button
            key={item}
            onClick={() => setTab(item)}
            className={`rounded-xl px-4 py-2 text-xs font-bold ${tab === item ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}
          >
            {item}
          </button>
        ))}
      </div>

      {tab === "Approvals" && (
        <ApprovalsPanel
          data={approvals.data ?? []}
          loading={approvals.isLoading}
          onDecision={(id, decision) => decide.mutate({ id, decision })}
        />
      )}
      {tab === "Scoped access" && (
        <ScopesPanel data={scopes.data ?? []} onChanged={refresh} />
      )}
      {tab === "RBAC changes" && (
        <RbacPanel data={rbac.data ?? []} onChanged={refresh} />
      )}
      {tab === "Invitations" && (
        <InvitationsPanel data={invitations.data ?? []} onChanged={refresh} />
      )}
      {tab === "Sessions" && (
        <SessionsPanel
          data={sessions.data ?? []}
          currentUserId={user?.id}
          onRevoke={(id) => revokeSession.mutate(id)}
        />
      )}
      {tab === "Admin MFA" && <MfaPanel enabled={Boolean(user?.mfaEnabled)} />}
      {tab === "SSO" && <SsoPanel data={sso.data ?? []} onChanged={refresh} />}
    </div>
  );
}

function ApprovalsPanel({
  data,
  loading,
  onDecision,
}: {
  data: any[];
  loading: boolean;
  onDecision: (id: string, decision: "APPROVE" | "REJECT") => void;
}) {
  if (loading) return <Panel>Loading approvals…</Panel>;
  return (
    <div className="space-y-3">
      {data.length ? (
        data.map((item) => (
          <Panel key={item.id}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-500" />
                  <span className="font-bold text-slate-900">
                    {item.requestType}
                  </span>
                  <Status value={item.status} />
                  <Status value={item.riskLevel} />
                </div>
                <p className="mt-2 text-sm text-slate-600">{item.reason}</p>
                <p className="mt-1 text-xs text-slate-400">
                  Requested by {item.requestedBy?.email} ·{" "}
                  {new Date(item.createdAt).toLocaleString()}
                </p>
              </div>
              {item.status === "PENDING" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => onDecision(item.id, "APPROVE")}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Approve
                  </button>
                  <button
                    onClick={() => onDecision(item.id, "REJECT")}
                    className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-700"
                  >
                    <X className="h-3.5 w-3.5" />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </Panel>
        ))
      ) : (
        <Panel>No approval requests.</Panel>
      )}
    </div>
  );
}

function ScopesPanel({
  data,
  onChanged,
}: {
  data: any[];
  onChanged: () => void;
}) {
  const [form, setForm] = useState({
    platformUserId: "",
    scopeType: "TENANT",
    scopeValue: "",
    capabilities: "TENANT_VIEW",
    reason: "",
    expiresAt: "",
    breakGlass: false,
  });
  const create = useMutation({
    mutationFn: () =>
      platformControlService.createScopeGrant({
        ...form,
        capabilities: form.capabilities
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        expiresAt: form.expiresAt || undefined,
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Scope grant submitted for approval");
    },
    onError: () => toast.error("Scope grant request failed"),
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      platformControlService.revokeScopeGrant(
        id,
        "Administrator revoked scoped platform access",
      ),
    onSuccess: () => {
      onChanged();
      toast.success("Scope grant revoked");
    },
    onError: () => toast.error("Scope grant could not be revoked"),
  });
  return (
    <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
      <Panel>
        <h3 className="font-bold">Request scoped access</h3>
        <div className="mt-4 space-y-3">
          <Input
            label="Platform user UUID"
            value={form.platformUserId}
            onChange={(value) => setForm({ ...form, platformUserId: value })}
          />
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
              Scope type
            </span>
            <select
              value={form.scopeType}
              onChange={(event) =>
                setForm({ ...form, scopeType: event.target.value })
              }
              className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
            >
              <option>TENANT</option>
              <option>PARTNER</option>
              <option>REGION</option>
              <option>COHORT</option>
            </select>
          </label>
          <Input
            label="Scope value (tenant / partner / region)"
            value={form.scopeValue}
            onChange={(value) => setForm({ ...form, scopeValue: value })}
          />
          <Input
            label="Capabilities (comma separated)"
            value={form.capabilities}
            onChange={(value) => setForm({ ...form, capabilities: value })}
          />
          <Input
            label="Expiry (ISO date)"
            value={form.expiresAt}
            onChange={(value) => setForm({ ...form, expiresAt: value })}
          />
          <Input
            label="Reason"
            value={form.reason}
            onChange={(value) => setForm({ ...form, reason: value })}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.breakGlass}
              onChange={(event) =>
                setForm({ ...form, breakGlass: event.target.checked })
              }
            />
            Expiring break-glass access
          </label>
          <button
            disabled={form.reason.length < 8 || create.isPending}
            onClick={() => create.mutate()}
            className="w-full rounded-xl bg-slate-900 py-2.5 text-xs font-bold text-white disabled:opacity-50"
          >
            Submit for approval
          </button>
        </div>
      </Panel>
      <div className="space-y-3">
        {data.map((grant) => (
          <Panel key={grant.id}>
            <div className="flex justify-between gap-3">
              <div>
                <p className="font-bold">{grant.platformUser?.email}</p>
                <p className="text-xs text-slate-500">
                  {grant.scopeType}: {grant.scopeValue}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {grant.capabilities.join(", ")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Status
                  value={
                    grant.revokedAt
                      ? "REVOKED"
                      : grant.approvedAt
                        ? "ACTIVE"
                        : "PENDING"
                  }
                />
                {grant.approvedAt && !grant.revokedAt && (
                  <button
                    onClick={() => revoke.mutate(grant.id)}
                    className="rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-700"
                  >
                    Revoke
                  </button>
                )}
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function RbacPanel({
  data,
  onChanged,
}: {
  data: any[];
  onChanged: () => void;
}) {
  const [form, setForm] = useState({
    name: "",
    targetType: "ROLE",
    targetId: "",
    permissionKeys: "",
    reason: "",
  });
  const create = useMutation({
    mutationFn: () =>
      platformControlService.createRbacChangeSet({
        ...form,
        permissionKeys: form.permissionKeys
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      onChanged();
      setForm({
        name: "",
        targetType: "ROLE",
        targetId: "",
        permissionKeys: "",
        reason: "",
      });
      toast.success("RBAC draft created with impact preview");
    },
    onError: () => toast.error("RBAC draft could not be created"),
  });
  const action = useMutation({
    mutationFn: async ({
      id,
      actionName,
    }: {
      id: string;
      actionName: "submit" | "approve" | "reject" | "apply" | "rollback";
    }) => {
      const reason =
        window.prompt(`${actionName} note (minimum 8 characters)`) || "";
      if (reason.length < 8) throw new Error("A reason is required");
      if (actionName === "submit")
        return platformControlService.submitRbacChangeSet(id, reason);
      if (actionName === "approve" || actionName === "reject")
        return platformControlService.reviewRbacChangeSet(
          id,
          actionName === "approve" ? "APPROVE" : "REJECT",
          reason,
        );
      if (actionName === "apply")
        return platformControlService.applyRbacChangeSet(id, reason);
      return platformControlService.rollbackRbacChangeSet(id, reason);
    },
    onSuccess: () => {
      onChanged();
      toast.success("RBAC change set updated");
    },
    onError: () => toast.error("RBAC action was not accepted"),
  });
  return (
    <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
      <Panel>
        <h3 className="font-bold">Draft RBAC change</h3>
        <p className="mt-1 text-xs text-slate-500">
          The server calculates additions/removals before a separate admin
          reviews and applies the change.
        </p>
        <div className="mt-4 space-y-3">
          <Input
            label="Change set name"
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
          />
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
              Target type
            </span>
            <select
              value={form.targetType}
              onChange={(event) =>
                setForm({ ...form, targetType: event.target.value })
              }
              className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
            >
              <option>ROLE</option>
              <option>DEFAULT_ROLE_TEMPLATE</option>
            </select>
          </label>
          <Input
            label="Target UUID"
            value={form.targetId}
            onChange={(value) => setForm({ ...form, targetId: value })}
          />
          <Input
            label="Desired permission keys (comma separated)"
            value={form.permissionKeys}
            onChange={(value) => setForm({ ...form, permissionKeys: value })}
          />
          <Input
            label="Reason"
            value={form.reason}
            onChange={(value) => setForm({ ...form, reason: value })}
          />
          <button
            disabled={
              form.name.length < 3 ||
              !form.targetId ||
              form.reason.length < 8 ||
              create.isPending
            }
            onClick={() => create.mutate()}
            className="w-full rounded-xl bg-slate-900 py-2.5 text-xs font-bold text-white disabled:opacity-50"
          >
            Create draft & preview
          </button>
        </div>
      </Panel>
      <div className="space-y-3">
        {data.length ? (
          data.map((changeSet) => {
            const preview = changeSet.preview ?? {};
            return (
              <Panel key={changeSet.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold">{changeSet.name}</p>
                      <Status value={changeSet.status} />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {changeSet.targetType} · {changeSet.targetId}
                    </p>
                    <p className="mt-2 text-xs text-emerald-700">
                      Add: {preview.add?.join(", ") || "none"}
                    </p>
                    <p className="text-xs text-red-600">
                      Remove: {preview.remove?.join(", ") || "none"}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {changeSet.status === "DRAFT" && (
                      <Action
                        onClick={() =>
                          action.mutate({
                            id: changeSet.id,
                            actionName: "submit",
                          })
                        }
                      >
                        Submit
                      </Action>
                    )}
                    {changeSet.status === "PENDING_APPROVAL" && (
                      <>
                        <Action
                          onClick={() =>
                            action.mutate({
                              id: changeSet.id,
                              actionName: "approve",
                            })
                          }
                        >
                          Approve
                        </Action>
                        <Action
                          danger
                          onClick={() =>
                            action.mutate({
                              id: changeSet.id,
                              actionName: "reject",
                            })
                          }
                        >
                          Reject
                        </Action>
                      </>
                    )}
                    {changeSet.status === "APPROVED" && (
                      <Action
                        onClick={() =>
                          action.mutate({
                            id: changeSet.id,
                            actionName: "apply",
                          })
                        }
                      >
                        Apply
                      </Action>
                    )}
                    {changeSet.status === "APPLIED" && (
                      <Action
                        danger
                        onClick={() =>
                          action.mutate({
                            id: changeSet.id,
                            actionName: "rollback",
                          })
                        }
                      >
                        Rollback
                      </Action>
                    )}
                  </div>
                </div>
              </Panel>
            );
          })
        ) : (
          <Panel>No RBAC change sets.</Panel>
        )}
      </div>
    </div>
  );
}

function InvitationsPanel({
  data,
  onChanged,
}: {
  data: any[];
  onChanged: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("SUPPORT_AGENT");
  const [token, setToken] = useState("");
  const create = useMutation({
    mutationFn: () => platformControlService.createInvitation({ email, role }),
    onSuccess: (result) => {
      setToken(result.invitationToken);
      onChanged();
      toast.success("Invitation created; copy the token now");
    },
  });
  return (
    <div className="space-y-4">
      <Panel>
        <div className="grid gap-3 md:grid-cols-[1fr_220px_auto]">
          <Input label="Email" value={email} onChange={setEmail} />
          <label>
            <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
              Role
            </span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value)}
              className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
            >
              <option>SUPPORT_AGENT</option>
              <option>SUPPORT_ADMIN</option>
              <option>CHANNEL_PARTNER</option>
            </select>
          </label>
          <button
            disabled={!email || create.isPending}
            onClick={() => create.mutate()}
            className="mt-5 inline-flex h-10 items-center gap-2 rounded-xl bg-slate-900 px-4 text-xs font-bold text-white"
          >
            <UserPlus className="h-4 w-4" />
            Invite
          </button>
        </div>
        {token && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-bold text-amber-800">
              One-time invitation token
            </p>
            <code className="mt-1 block break-all text-xs">{token}</code>
          </div>
        )}
      </Panel>
      <div className="space-y-2">
        {data.map((invite) => (
          <Panel key={invite.id}>
            <div className="flex justify-between">
              <div>
                <p className="font-bold">{invite.email}</p>
                <p className="text-xs text-slate-500">
                  {invite.role} · expires{" "}
                  {new Date(invite.expiresAt).toLocaleString()}
                </p>
              </div>
              <Status
                value={
                  invite.acceptedAt
                    ? "ACCEPTED"
                    : invite.revokedAt
                      ? "REVOKED"
                      : new Date(invite.expiresAt) < new Date()
                        ? "EXPIRED"
                        : "PENDING"
                }
              />
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function SessionsPanel({
  data,
  currentUserId,
  onRevoke,
}: {
  data: any[];
  currentUserId?: string;
  onRevoke: (id: string) => void;
}) {
  return (
    <div className="space-y-3">
      {data.map((session) => (
        <Panel key={session.id}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-bold">
                {session.platformUser?.email}
                {session.platformUserId === currentUserId && (
                  <span className="ml-2 text-xs text-blue-600">
                    Current user
                  </span>
                )}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {session.deviceName || session.sourceDevice || "Unknown device"}{" "}
                · {session.ipAddress || "No IP"}
              </p>
              <p className="text-xs text-slate-400">
                Last used {new Date(session.lastUsedAt).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Status
                value={
                  session.revokedAt
                    ? "REVOKED"
                    : new Date(session.expiresAt) < new Date()
                      ? "EXPIRED"
                      : "ACTIVE"
                }
              />
              {!session.revokedAt && (
                <button
                  onClick={() => onRevoke(session.id)}
                  className="rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-700"
                >
                  Revoke
                </button>
              )}
            </div>
          </div>
        </Panel>
      ))}
    </div>
  );
}

function MfaPanel({ enabled }: { enabled: boolean }) {
  const [setup, setSetup] = useState<{
    secret: string;
    recoveryCodes: string[];
    otpauthUri: string;
  } | null>(null);
  const [code, setCode] = useState("");
  const begin = useMutation({
    mutationFn: () => authService.setupMfa("Primary authenticator"),
    onSuccess: setSetup,
  });
  const verify = useMutation({
    mutationFn: () => authService.verifyMfa(code),
    onSuccess: () => {
      toast.success("MFA enabled. Store recovery codes safely.");
      useAuthStore.setState((state) => ({
        user: state.user ? { ...state.user, mfaEnabled: true } : null,
      }));
    },
  });
  const disable = useMutation({
    mutationFn: () => authService.disableMfa(code),
    onSuccess: () => {
      toast.success("MFA disabled and sessions revoked");
      useAuthStore.getState().logout();
    },
  });
  return (
    <Panel>
      <div className="flex items-center gap-3">
        <KeyRound className="h-5 w-5 text-blue-600" />
        <div>
          <h3 className="font-bold">Authenticator MFA</h3>
          <p className="text-xs text-slate-500">
            {enabled ? "Enabled on this account" : "Not configured"}
          </p>
        </div>
      </div>
      {!enabled && !setup && (
        <button
          onClick={() => begin.mutate()}
          className="mt-5 rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white"
        >
          Set up TOTP
        </button>
      )}
      {setup && (
        <div className="mt-5 space-y-4">
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold">Authenticator secret</p>
            <code className="text-sm">{setup.secret}</code>
            <p className="mt-2 break-all text-[10px] text-slate-400">
              {setup.otpauthUri}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold">Recovery codes — shown once</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {setup.recoveryCodes.map((item) => (
                <code key={item} className="rounded bg-amber-50 p-2 text-xs">
                  {item}
                </code>
              ))}
            </div>
          </div>
        </div>
      )}{" "}
      {(setup || enabled) && (
        <div className="mt-5 flex max-w-md gap-2">
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="Authenticator or recovery code"
            className="h-10 flex-1 rounded-xl border border-slate-200 px-3 text-sm"
          />
          <button
            disabled={code.length < 6}
            onClick={() =>
              setup && !enabled ? verify.mutate() : disable.mutate()
            }
            className={`rounded-xl px-4 text-xs font-bold text-white ${enabled ? "bg-red-600" : "bg-emerald-600"}`}
          >
            {enabled ? "Disable" : "Verify"}
          </button>
        </div>
      )}
    </Panel>
  );
}

function SsoPanel({ data, onChanged }: { data: any[]; onChanged: () => void }) {
  const [form, setForm] = useState({
    name: "",
    protocol: "OIDC",
    issuer: "",
    clientId: "",
    secretReference: "",
    authorizationUrl: "",
    tokenUrl: "",
    jwksUrl: "",
    allowedDomains: "",
    defaultRole: "SUPPORT_AGENT",
  });
  const create = useMutation({
    mutationFn: () =>
      platformControlService.createSsoProvider({
        ...form,
        secretReference: form.secretReference || undefined,
        authorizationUrl: form.authorizationUrl || undefined,
        tokenUrl: form.tokenUrl || undefined,
        jwksUrl: form.jwksUrl || undefined,
        allowedDomains: form.allowedDomains
          .split(",")
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean),
      }),
    onSuccess: () => {
      onChanged();
      toast.success("SSO provider registered in disabled state");
    },
    onError: () => toast.error("SSO provider could not be registered"),
  });
  const status = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      platformControlService.setSsoProviderStatus(
        id,
        isActive,
        isActive
          ? "Administrator enabled verified SSO provider"
          : "Administrator disabled SSO provider",
      ),
    onSuccess: () => {
      onChanged();
      toast.success("SSO provider status updated");
    },
    onError: () => toast.error("SSO provider status was not changed"),
  });
  return (
    <div className="grid gap-5 lg:grid-cols-[420px_1fr]">
      <Panel>
        <h3 className="font-bold">Register identity provider</h3>
        <p className="mt-1 text-xs text-slate-500">
          Credentials stay in the secret manager; store only its reference.
        </p>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Provider name"
              value={form.name}
              onChange={(value) => setForm({ ...form, name: value })}
            />
            <label>
              <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
                Protocol
              </span>
              <select
                value={form.protocol}
                onChange={(event) =>
                  setForm({ ...form, protocol: event.target.value })
                }
                className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
              >
                <option>OIDC</option>
                <option>SAML</option>
              </select>
            </label>
          </div>
          <Input
            label="Issuer / entity ID"
            value={form.issuer}
            onChange={(value) => setForm({ ...form, issuer: value })}
          />
          <Input
            label="Client ID"
            value={form.clientId}
            onChange={(value) => setForm({ ...form, clientId: value })}
          />
          <Input
            label="Secret reference"
            value={form.secretReference}
            onChange={(value) => setForm({ ...form, secretReference: value })}
          />
          {form.protocol === "OIDC" && (
            <>
              <Input
                label="Authorization URL"
                value={form.authorizationUrl}
                onChange={(value) =>
                  setForm({ ...form, authorizationUrl: value })
                }
              />
              <Input
                label="Token URL"
                value={form.tokenUrl}
                onChange={(value) => setForm({ ...form, tokenUrl: value })}
              />
              <Input
                label="JWKS URL"
                value={form.jwksUrl}
                onChange={(value) => setForm({ ...form, jwksUrl: value })}
              />
            </>
          )}
          <Input
            label="Allowed domains (comma separated)"
            value={form.allowedDomains}
            onChange={(value) => setForm({ ...form, allowedDomains: value })}
          />
          <label>
            <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
              Default role
            </span>
            <select
              value={form.defaultRole}
              onChange={(event) =>
                setForm({ ...form, defaultRole: event.target.value })
              }
              className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
            >
              <option>SUPPORT_AGENT</option>
              <option>SUPPORT_ADMIN</option>
              <option>CHANNEL_PARTNER</option>
            </select>
          </label>
          <button
            disabled={
              !form.name || !form.issuer || !form.clientId || create.isPending
            }
            onClick={() => create.mutate()}
            className="w-full rounded-xl bg-slate-900 py-2.5 text-xs font-bold text-white disabled:opacity-50"
          >
            Register disabled provider
          </button>
        </div>
      </Panel>
      <div className="space-y-3">
        {data.length ? (
          data.map((provider) => (
            <Panel key={provider.id}>
              <div className="flex justify-between gap-4">
                <div>
                  <p className="font-bold">{provider.name}</p>
                  <p className="text-xs text-slate-500">
                    {provider.protocol} · {provider.issuer}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Domains: {provider.allowedDomains.join(", ") || "none"} ·
                    Default role: {provider.defaultRole}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Status value={provider.isActive ? "ACTIVE" : "DISABLED"} />
                  <button
                    onClick={() =>
                      status.mutate({
                        id: provider.id,
                        isActive: !provider.isActive,
                      })
                    }
                    className={`rounded-lg px-3 py-2 text-xs font-bold ${provider.isActive ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}
                  >
                    {provider.isActive ? "Disable" : "Enable"}
                  </button>
                </div>
              </div>
            </Panel>
          ))
        ) : (
          <Panel>No OIDC or SAML provider configured.</Panel>
        )}
      </div>
    </div>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {children}
    </section>
  );
}
function Input({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
      />
    </label>
  );
}
function Status({ value }: { value: string }) {
  const color = ["ACTIVE", "APPROVED", "ACCEPTED", "LOW"].includes(value)
    ? "bg-emerald-50 text-emerald-700"
    : ["PENDING", "MEDIUM"].includes(value)
      ? "bg-amber-50 text-amber-700"
      : ["HIGH", "CRITICAL", "REJECTED", "REVOKED"].includes(value)
        ? "bg-red-50 text-red-700"
        : "bg-slate-100 text-slate-600";
  return (
    <span
      className={`rounded-full px-2 py-1 text-[9px] font-bold uppercase ${color}`}
    >
      {value}
    </span>
  );
}

function Action({
  children,
  onClick,
  danger = false,
}: {
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg px-3 py-2 text-xs font-bold ${danger ? "bg-red-50 text-red-700" : "bg-slate-900 text-white"}`}
    >
      {children}
    </button>
  );
}
